import { SUPPORTED_LOCALES } from '../i18n/config';
import { isGaisukePublicPath } from './gaisukePath';
import { isKeibaArenaPublicPath } from './keibaArenaPath';
import { isBanquetPublicPath } from './banquetPath';
import { isEconReportPublicPath } from './econReportPath';
import { isGaisukeFxPublicPath } from './gaisukeFxPath';
import {
  isGeneAnalysisPublicEnabled,
  isLaboSukePublicEnabled,
  isSamplePublicEnabled,
  isTppPublicEnabled,
} from './config';
import { isKeisukePublicPathname } from './keisukePath';
import { isLaboSukePublicPathname } from './laboSukePath';

export function isLaboSukePublicPath(pathname: string): boolean {
  return isLaboSukePublicPathname(pathname);
}

export function isTppPublicPathname(pathname: string): boolean {
  if (!isTppPublicEnabled()) return false;
  const path = pathname.replace(/\/+$/, '') || '/';
  return path === '/transit-pass-pool';
}

/** 未ログインでサンプル管理（Zip）を開くパス */
export function isSamplePublicPathname(pathname: string): boolean {
  if (!isSamplePublicEnabled()) return false;
  const path = normalizePathname(pathname);
  if (path === '/samples' || path.startsWith('/samples/')) {
    // 公開共有 URL は別系統（常に API）
    if (path.startsWith('/samples/encrypted/') || path.startsWith('/samples/share/')) {
      return false;
    }
    // 帳票・入出力は DB／管理者向けのためゲスト Zip 対象外
    if (
      path === '/samples/reports' ||
      path.startsWith('/excel-') ||
      path === '/reports'
    ) {
      return false;
    }
    return true;
  }
  if (path === '/storage-rooms') return true;
  return false;
}

function normalizePathname(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

/** 遺伝子解析をログイン不要で公開するとき（VITE_GENE_ANALYSIS_PUBLIC_ENABLED） */
export function isGeneAnalysisPublicPathname(pathname: string): boolean {
  if (!isGeneAnalysisPublicEnabled()) return false;
  const path = normalizePathname(pathname);
  return path === '/gene-analysis' || path.startsWith('/gene-analysis/');
}

/** 未ログインでも利用できるデモ・共有ページ（401 時に /login へ飛ばさない） */
export function isPublicDemoAccessPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  if (isGaisukePublicPath(path)) return true;
  if (isKeibaArenaPublicPath(path)) return true;
  if (isBanquetPublicPath(path)) return true;
  if (isEconReportPublicPath(path)) return true;
  if (isGaisukeFxPublicPath(path)) return true;
  if (isLaboSukePublicPathname(path)) return true;
  if (isKeisukePublicPathname(path)) return true;
  if (isTppPublicPathname(path)) return true;
  if (isGeneAnalysisPublicPathname(path)) return true;
  if (isSamplePublicPathname(path)) return true;
  if (path === '/reservations/share' || path.startsWith('/reservations/share/')) {
    return true;
  }
  return false;
}

/** api.ts 401 ハンドラ用 — トップ・認証画面等 */
export function isPublicAppPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  const publicRoutes = [
    '/',
    '/login',
    '/register',
    '/password-reset',
    '/help',
    '/privacy',
    '/terms',
    '/500',
    '/contact',
    '/announcements',
    '/usage-record',
    '/feature-requests',
    '/reservations/share',
    '/gaisuke',
    '/keiba-arena',
    '/arena/keiba',
    '/arena/banquet',
    '/arena/econ',
    '/arena/fx',
    '/labo-suke',
    '/keisuke',
    '/transit-pass-pool',
  ];
  const isLocalePublicPage =
    /^\/(ja|en|zh-CN|fr|es|de|pt|ru)\/(help|contact|announcements|gaisuke|keiba-arena|labo-suke|keisuke)$/.test(path) ||
    /^\/(ja|en|zh-CN|fr|es|de|pt|ru)\/arena\/keiba$/.test(path) ||
    /^\/(ja|en|zh-CN|fr|es|de|pt|ru)\/arena\/banquet(\/.*)?$/.test(path) ||
    /^\/(ja|en|zh-CN|fr|es|de|pt|ru)\/arena\/econ(\/.*)?$/.test(path) ||
    /^\/(ja|en|zh-CN|fr|es|de|pt|ru)\/arena\/fx$/.test(path) ||
    /^\/(ja|en|zh-CN|fr|es|de|pt|ru)\/keisuke\//.test(path);
  const isLocaleHome = /^\/(ja|en|zh-CN|fr|es|de|pt|ru)$/.test(path);
  if (/^\/samples\/encrypted\//.test(path)) return true;
  return (
    publicRoutes.includes(path) ||
    isLocalePublicPage ||
    isLocaleHome ||
    isPublicDemoAccessPath(path)
  );
}

/**
 * 認証必須のアプリルート（未ログイン時は /login へ誘導）。
 * それ以外の未知パス（スパム URL 等）は 404 とし、Search Console の「リダイレクト」報告を減らす。
 */
export function isProtectedAppPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  if (isPublicAppPath(path) || isPublicDemoAccessPath(path)) {
    return false;
  }

  const protectedPatterns: RegExp[] = [
    /^\/dashboard$/,
    /^\/samples(\/|$)/,
    /^\/keisuke(\/|$)/,
    /^\/admin(\/|$)/,
    /^\/billing(\/|$)/,
    /^\/profile$/,
    /^\/users$/,
    /^\/storage-rooms$/,
    /^\/excel-import$/,
    /^\/excel-export$/,
    /^\/reports$/,
    /^\/settings$/,
    /^\/gene-analysis(\/|$)/,
    /^\/gaisuke\/settings$/,
    /^\/arena\/econ\/compose$/,
    /^\/reservations$/,
    /^\/reservations\/[^/]+$/,
    /^\/labo-suke$/,
    /^\/access-logs$/,
    /^\/conversion-funnel$/,
    /^\/user-activity-logs$/,
    /^\/database-backup$/,
  ];

  if (path.startsWith('/reservations/share')) {
    return false;
  }
  if (path === '/labo-suke' && isLaboSukePublicEnabled()) {
    return false;
  }
  if (isLaboSukePublicPathname(path)) {
    return false;
  }
  if (isKeisukePublicPathname(path)) {
    return false;
  }
  if (isGeneAnalysisPublicPathname(path)) {
    return false;
  }
  if (isGaisukePublicPath(path)) {
    return false;
  }
  if (/^\/samples\/encrypted\//.test(path)) {
    return false;
  }
  if (/^\/samples\/share\//.test(path)) {
    return false;
  }
  if (isSamplePublicPathname(path)) {
    return false;
  }

  return protectedPatterns.some((pattern) => pattern.test(path));
}

/**
 * Home の canonical / PageMeta path（URL パスを正本とする）。
 * ja の正規は `/`（`/ja` は重複扱いのため空文字＝ルート canonical）。
 */
export function homeCanonicalSegment(pathname: string): string {
  const segment = normalizePathname(pathname).split('/').filter(Boolean)[0];
  if (segment === 'ja') {
    return '';
  }
  if (segment && (SUPPORTED_LOCALES as readonly string[]).includes(segment)) {
    return segment;
  }
  return '';
}
